import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { ethers } from 'ethers';
import Upload from './pages/Upload';
import Paramedic from './pages/Paramedic';
import AuditLog from './pages/AuditLog';

function Navigation({ account, connectWallet, disconnectWallet }) {
  const location = useLocation();
  const [showDisconnect, setShowDisconnect] = useState(false);

  const handleDisconnect = () => {
    if (window.confirm('Are you sure you want to disconnect your wallet?')) {
      disconnectWallet();
      setShowDisconnect(false);
    }
  };

  return (
    <header className="header">
      <div className="container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '20px' }}>
          <h1 style={{ margin: 0, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontSize: '28px' }}>
            🏥 MedVault
          </h1>
          <nav className="nav">
            <Link to="/" className={location.pathname === '/' ? 'active' : ''}>
              Upload
            </Link>
            <Link to="/paramedic" className={location.pathname === '/paramedic' ? 'active' : ''}>
              Access
            </Link>
            <Link to="/audit" className={location.pathname === '/audit' ? 'active' : ''}>
              Audit Log
            </Link>
          </nav>
          {account ? (
            <div 
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                position: 'relative'
              }}
            >
              <div 
                onClick={() => setShowDisconnect(!showDisconnect)}
                style={{
                  background: '#f3f4f6',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  border: '1px solid #e5e7eb',
                  transition: 'all 0.2s ease',
                }}
              >
                <span>{account.slice(0, 6)}...{account.slice(-4)}</span>
                <span style={{ fontSize: '12px' }}>▼</span>
              </div>
              
              {showDisconnect && (
                <div 
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '8px',
                    background: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    overflow: 'hidden',
                    zIndex: 10,
                    minWidth: '160px'
                  }}
                >
                  <button 
                    onClick={handleDisconnect}
                    style={{
                      width: '100%',
                      padding: '8px 16px',
                      border: 'none',
                      background: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      color: '#ef4444',
                    }}
                  >
                    Disconnect Wallet
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button onClick={connectWallet} className="btn btn-primary">
              Connect Wallet
            </button>
          )}
        </div>
      </div>
    </header>
  );
}

function App() {
  const [account, setAccount] = useState(() => {
    const savedAccount = localStorage.getItem('medvault_connectedAccount');
    return savedAccount || null;
  });

  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);

  const disconnectWallet = useCallback(() => {
    setAccount(null);
    setProvider(null);
    setSigner(null);
    localStorage.removeItem('medvault_connectedAccount');
    // Also clear any MetaMask state
    if (window.ethereum) {
      window.ethereum.request({
        method: 'wallet_requestPermissions',
        params: [{ eth_accounts: {} }]
      }).then(() => {
        window.ethereum.request({
          method: 'eth_requestAccounts',
          params: []
        }).catch(() => {});
      }).catch(() => {});
    }
  }, []);

  const connectWallet = useCallback(async () => {
    try {
      if (!window.ethereum) {
        alert('Please install MetaMask to use this application');
        return;
      }

      const accounts = await window.ethereum.request({
        method: 'eth_requestAccounts'
      });

      const ethersProvider = new ethers.BrowserProvider(window.ethereum);
      const ethersSigner = await ethersProvider.getSigner();

      setAccount(accounts[0]);
      setProvider(ethersProvider);
      setSigner(ethersSigner);
      localStorage.setItem('medvault_connectedAccount', accounts[0]);

      const chainId = await window.ethereum.request({ method: 'eth_chainId' });
      const expectedChainId = '0x413'; // 1043 in hex

      if (chainId !== expectedChainId) {
        try {
          await window.ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: expectedChainId }],
          });
        } catch (switchError) {
          if (switchError.code === 4902) {
            await window.ethereum.request({
              method: 'wallet_addEthereumChain',
              params: [{
                chainId: expectedChainId,
                chainName: 'BlockDAG Awakening',
                nativeCurrency: {
                  name: 'BDAG',
                  symbol: 'BDAG',
                  decimals: 18
                },
                rpcUrls: ['https://rpc.awakening.bdagscan.com'],
                blockExplorerUrls: ['https://bdagscan.com']
              }]
            });
          }
        }
      }
    } catch (error) {
      console.error('Failed to connect wallet:', error);
      alert('Failed to connect wallet: ' + error.message);
    }
  }, []);

  useEffect(() => 
    {
    const handleAccountsChanged = (accounts) => {
      if (accounts.length === 0) {
        disconnectWallet();
      } else if (accounts[0] !== account) {
        setAccount(accounts[0]);
        localStorage.setItem('medvault_connectedAccount', accounts[0]);
      }
    };

    const handleChainChanged = () => {
      window.location.reload();
    };

    const checkConnectedAccount = async () => {
      if (window.ethereum) {
        try {
          const accounts = await window.ethereum.request({
            method: 'eth_accounts'
          });
          
          // Only restore if no account is currently set
          if (accounts.length > 0 && !account) {
            const ethersProvider = new ethers.BrowserProvider(window.ethereum);
            const ethersSigner = await ethersProvider.getSigner();
            
            setAccount(accounts[0]);
            setProvider(ethersProvider);
            setSigner(ethersSigner);
            localStorage.setItem('medvault_connectedAccount', accounts[0]);
          } else if (accounts.length === 0 && account) {
            // Clear state if MetaMask has no accounts but app does
            disconnectWallet();
          }
        } catch (error) {
          console.error('Error checking connected accounts:', error);
        }
      }
    };

    // Initialize event listeners and check account
    if (window.ethereum) {
      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);
    }

  // Cleanup function
    return () => {
      if (window.ethereum) {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
        window.ethereum.removeListener('chainChanged', handleChainChanged);
      }
    };
  }, []);

  return (
    <Router>
      <div style={{ minHeight: '100vh' }}>
      <Navigation 
        account={account} 
        connectWallet={connectWallet} 
        disconnectWallet={disconnectWallet}
      />
        <div className="container">
          <Routes>
            <Route path="/" element={<Upload account={account} signer={signer} provider={provider} />} />
            <Route path="/paramedic" element={<Paramedic account={account} signer={signer} provider={provider} />} />
            <Route path="/audit" element={<AuditLog provider={provider} />} />
          </Routes>
        </div>
      </div>
    </Router>
  );
}

export default App;